/**
 * TestLikeAMobileModal.tsx
 *
 * Modal for "Test Like A Mobile" interactive phone UI.
 * Users select eSIM + Location, then interact with a generic Android phone interface
 * to make calls, send SMS, and browse the internet using a real modem.
 *
 * Features:
 * - eSIM profile selection
 * - Location selection (hierarchical)
 * - Lock acquisition on open
 * - Phone frame with app launcher
 * - Lock release on close
 * - Session expiry warnings
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui';
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { auth, db as firestoreDb } from '@/config/firebase';
import { useToast } from '@/contexts/ToastContext';
import { PhoneFrame, type PhoneNavHandlers } from './PhoneFrame';
import { AppLauncher } from './AppLauncher';
import phoneWebSocketService from '@/services/phoneWebSocketService';
import {
  startTlamSession,
  prepareTlamSession,
  heartbeatTlamSession,
  endTlamSession,
  endTlamSessionBeacon,
  type TlamSessionStartResponse,
} from '@/services/tlamSessionService';
import { getProfilesForOrg, getSharedProfiles, sharedGroupsForOrg } from '@/services/esimService';
import { canOrgUseSharedEsims, sharedPoolSentinel } from '@simcheck/shared-types';
import { getLocations, filterLocationsByTenancy } from '@/services/hierarchicalLocationService';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import type { ESimProfile, Location, CentralEdgeDevice, CentralEdgeModem, ForcedRat } from '@simcheck/shared-types';
import { useAuth } from '@/contexts/AuthContext';
import { AlertCircle, Loader2, Smartphone, MapPin, Server, CreditCard, Lock, X, Unlock } from 'lucide-react';
import { cn } from '@/lib/utils';

// Keep-alive every 10 s. The server keeps the allocation's expiresAt only
// ~2 min ahead per beat, so a crashed/closed client is detected and released
// by the sweep within ~3-4 min (vs the old 5-min beat + 15-min TTL = up to
// half an hour of a dead session starving its modem). Hidden-tab timer
// throttling (~1/min floor) still keeps the window comfortably alive.
const HEARTBEAT_INTERVAL_MS = 10 * 1000;

export interface AutoStartConfig {
  eSimProfileId: string;
  locationId: string;
  // eSIM group selected in the Quick Tests wizard. When present, the TLAM
  // session-create sends this instead of a client-resolved eSimProfileId so the
  // backend resolves the right member for whichever modem it allocates
  // (reuse-on-modem else install-available). The client can't know the modem
  // up-front, so it can't pick the correct group member itself.
  eSimGroupId?: string;
  // Kept for backwards compatibility with existing callers. The backend's
  // /tlam/sessions endpoint picks the modem/device via the same findFreeModem
  // path scheduled flows use — these fields are no longer read by the modal.
  deviceId?: string;
  modemImei?: string;
  // Forced PLMN (MCCMNC). When the user picked a specific network in the
  // Quick Tests wizard, we carry that through so the TLAM session is locked
  // to the same network — otherwise the modem roams onto whatever PLMN the
  // automatic-selection algorithm prefers and the test becomes meaningless.
  forcedNetworkMccMnc?: string;
  // Forced RAT (Radio Access Technology). Same idea: backend filters
  // allocation by capability, then runs a vendor-specific RAT-pin command
  // on the Pi. Hard-fails the session on any lock error.
  forcedRat?: ForcedRat;
  // APN chosen in Quick Tests Step 3 (mandatory there). Passed to
  // startTlamSession so the backend warms the data bearer at allocation time
  // with the correct APN, and into phoneWebSocketService so PhoneBrowser uses
  // it without prompting. Single source of truth for the session's APN.
  apn?: string;
}

export interface TestLikeAMobileModalProps {
  isOpen: boolean;
  onClose: () => void;
  autoStart?: AutoStartConfig; // Optional auto-start configuration
}

/**
 * Step 1: eSIM, Location, Edge Device, and Modem Selection
 */
interface Step1SelectionProps {
  profiles: ESimProfile[];
  locations: Location[];
  edgeDevices: CentralEdgeDevice[];
  selectedProfileId: string | null;
  selectedLocationId: string | null;
  selectedDeviceId: string | null;
  selectedModemImei: string | null;
  onProfileChange: (profileId: string) => void;
  onLocationChange: (locationId: string) => void;
  onDeviceChange: (deviceId: string) => void;
  onModemChange: (modemImei: string) => void;
  onNext: () => void;
  loading: boolean;
}

const Step1Selection: React.FC<Step1SelectionProps> = ({
  profiles,
  locations,
  edgeDevices,
  selectedProfileId,
  selectedLocationId,
  selectedDeviceId,
  selectedModemImei,
  onProfileChange,
  onLocationChange,
  onDeviceChange,
  onModemChange,
  onNext,
  loading
}) => {
  const availableProfiles = profiles.filter(p => p.status === 'available');
  const { activeOrganizationId: orgId, activeOrganization, isSuperAdmin } = useAuth();

  // Tenancy (#202): hide other orgs' private locations; the org's own
  // locations (or locations hosting its private devices) group first.
  // Shared helper, not an inline copy — it also hard-scopes synthetic demo
  // locations to their owning org, above the super-admin bypass.
  const visibleLocations = filterLocationsByTenancy(locations, orgId, isSuperAdmin, {
    hasDeviceAt: (id) => edgeDevices.some(d => d.locationId === id),
  });
  const isMyLocation = (l: Location) =>
    !!orgId &&
    ((l.ownerOrgId === orgId) ||
      edgeDevices.some(d => (d as any).ownerOrgId === orgId && d.locationId === l.id));
  const myLocations = visibleLocations.filter(isMyLocation);
  const sharedLocations = visibleLocations.filter(l => !isMyLocation(l));

  // Filter edge devices by selected location
  const filteredDevices = selectedLocationId
    ? edgeDevices.filter(d => d.locationId === selectedLocationId && d.status !== 'offline')
    : edgeDevices.filter(d => d.status !== 'offline');

  // Get modems from selected edge device
  const selectedDevice = selectedDeviceId
    ? edgeDevices.find(d => d.id === selectedDeviceId || d.deviceId === selectedDeviceId)
    : null;
  const availableModems: CentralEdgeModem[] = selectedDevice?.modems || [];

  const canStartSession = selectedProfileId && selectedLocationId && selectedDeviceId && selectedModemImei;

  return (
    <div className="space-y-6 p-6">
      {/* eSIM Profile Selection */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Select eSIM Profile</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Choose an eSIM profile to use for testing
        </p>

        <Select value={selectedProfileId || ''} onValueChange={onProfileChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select eSIM profile..." />
          </SelectTrigger>
          <SelectContent>
            {availableProfiles.filter(p => !p.shared).map((profile) => (
              <SelectItem key={profile.id} value={profile.id}>
                {profile.name || profile.networkName}
              </SelectItem>
            ))}
            {sharedGroupsForOrg(availableProfiles.filter(p => p.shared), activeOrganization).map((g) => (
              <SelectItem key={g.groupId} value={sharedPoolSentinel(g.groupId)}>
                SimCheck Shared — {g.groupName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {availableProfiles.length === 0 && (
          <p className="text-sm text-status-warning mt-2">
            No available eSIM profiles. Please add profiles in the eSIM Inventory.
          </p>
        )}
      </div>

      {/* Location Selection */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Select Location</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Choose the location where your test will run
        </p>

        <Select value={selectedLocationId || ''} onValueChange={onLocationChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select location..." />
          </SelectTrigger>
          <SelectContent>
            {myLocations.length > 0 && (
              <SelectGroup>
                <SelectLabel>My locations</SelectLabel>
                {myLocations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}{location.city ? ` (${location.city})` : ''}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {sharedLocations.length > 0 && (
              <SelectGroup>
                {myLocations.length > 0 && <SelectLabel>Shared locations</SelectLabel>}
                {sharedLocations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}{location.city ? ` (${location.city})` : ''}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>

        {visibleLocations.length === 0 && (
          <p className="text-sm text-status-warning mt-2">
            No locations available. Please configure locations first.
          </p>
        )}
      </div>

      {/* Edge Device Selection */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Server className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Select Edge Device</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Choose the edge device with modems at this location
        </p>

        <Select
          value={selectedDeviceId || ''}
          onValueChange={onDeviceChange}
          disabled={!selectedLocationId}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={selectedLocationId ? "Select edge device..." : "Select a location first"} />
          </SelectTrigger>
          <SelectContent>
            {filteredDevices.map((device) => (
              <SelectItem key={device.id} value={device.id}>
                {device.name} - {device.status === 'online' ? '🟢 Online' : '🟡 ' + device.status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedLocationId && filteredDevices.length === 0 && (
          <p className="text-sm text-status-warning mt-2">
            No edge devices available at this location. Please assign an edge device.
          </p>
        )}
      </div>

      {/* Modem Selection */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Smartphone className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Select Modem</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Choose the modem to use for this session
        </p>

        <Select
          value={selectedModemImei || ''}
          onValueChange={onModemChange}
          disabled={!selectedDeviceId}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={selectedDeviceId ? "Select modem..." : "Select an edge device first"} />
          </SelectTrigger>
          <SelectContent>
            {availableModems.map((modem) => (
              <SelectItem key={modem.imei} value={modem.imei ?? ''}>
                {modem.model ?? 'Unknown Model'} - {modem.imei ?? 'Unknown IMEI'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedDeviceId && availableModems.length === 0 && (
          <p className="text-sm text-status-warning mt-2">
            No modems available on this edge device.
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          onClick={onNext}
          disabled={!canStartSession || loading}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Acquiring Lock...
            </>
          ) : (
            <>
              <Smartphone className="w-4 h-4 mr-2" />
              Start Session
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

/**
 * Step 2: Compact Phone UI with side icons
 * - No header, icons on the right side with tooltips
 * - Smaller phone frame that fits without scrolling
 */
interface Step2PhoneUIProps {
  networkStatus: any;
  isRegistered: boolean;
  registrationMessage: string;
  lockId: string | null;
  modemImei: string | null;
  modemModel: string | null;
  dataPlaneStatus: 'idle' | 'preparing' | 'ready' | 'failed';
  onEndSession: () => void;
}

// Phone dimensions. The TLAM browser streams a fixed 412×915 Chromium window
// (CAPTURE_W×CAPTURE_H — the Pixel 7 CSS viewport, so mobile sites lay out at
// their real breakpoint). The frontend <video> renders that stream with
// object-fit:contain, so unless the on-screen *video area* matches the capture
// aspect ratio the stream gets pillar/letter-boxed and the page looks squeezed
// into a too-narrow column ("stretched to a different resolution"). To avoid
// that we DERIVE the frame width from its height at the exact capture AR — the
// video area then matches 412:915 and the stream fills it 1:1 (uniform scale,
// no bars). Height tracks the viewport; width follows.
const CAPTURE_W = 412;
const CAPTURE_H = 915;
const CAPTURE_AR = CAPTURE_W / CAPTURE_H; // 0.4503
// PhoneFrame vertical chrome that sits OUTSIDE the streamed video: the OS nav
// bar (h-12 = 48 px) always renders; the in-frame status bar (h-6 = 24 px) is
// hidden once the session is live (hideStatusBar). We size against the live
// case (nav bar only) so the video area is exactly 412:915 while browsing.
const PHONE_NAV_BAR_PX = 48;
const PHONE_HEIGHT_MIN = 520;
const PHONE_HEIGHT_MAX = 900;
// Vertical budget the phone frame has to *give up* to everything around it:
// SessionInfoHeader (~32 px) + Step2 padding (24 px) + dialog border/shadow
// (~16 px) + browser chrome (~80 px) + breathing room. Under-reserving clips
// the phone's bottom OS nav bar out of the visible viewport.
const PHONE_VIEWPORT_RESERVE = 180;

const computePhoneHeight = (): number => {
  if (typeof window === 'undefined') return 720;
  return Math.max(PHONE_HEIGHT_MIN, Math.min(PHONE_HEIGHT_MAX, window.innerHeight - PHONE_VIEWPORT_RESERVE));
};

// Width that makes the video area (frame height minus the OS nav bar) match the
// 412:915 capture AR, so the streamed page fills the screen without distortion.
const computePhoneWidth = (height: number): number =>
  Math.round((height - PHONE_NAV_BAR_PX) * CAPTURE_AR);

/**
 * Compact single-line info strip shown above the TLAM phone frame once a
 * session is live. Everything the tester needs to identify the hardware
 * (IMEI, model, operator, tech, signal) in one row so the phone viewport
 * gets as much vertical space as possible.
 */
const SessionInfoHeader: React.FC<{
  networkStatus: any;
  modemImei: string | null;
  modemModel: string | null;
  dataPlaneStatus: 'idle' | 'preparing' | 'ready' | 'failed';
}> = ({ networkStatus, modemImei, modemModel, dataPlaneStatus }) => {
  const operator: string = networkStatus?.operator || '—';
  const technology: string = networkStatus?.technology || '—';
  // signalQuality is now a percentage (0..100) as produced by
  // phoneWebSocketService's live snapshot. null/undefined when unknown —
  // render "—" rather than guess.
  const signalPct = typeof networkStatus?.signalQuality === 'number'
    ? Math.max(0, Math.min(100, Math.round(networkStatus.signalQuality)))
    : null;
  const bars = signalPct !== null ? Math.max(0, Math.min(4, Math.ceil(signalPct / 25))) : 0;

  return (
    <div
      className="rounded-md border border-border bg-card text-foreground text-[11px] px-2 py-1 mb-1.5 w-full flex items-center gap-2"
    >
      <span className="font-mono tracking-tight truncate shrink" title={modemImei || ''}>
        {modemImei || '—'}
      </span>
      {modemModel && <span className="text-muted-foreground shrink-0">{modemModel}</span>}
      <span className="text-muted-foreground shrink-0">·</span>
      <span className="truncate shrink" title={operator}>{operator}</span>
      <span className="text-muted-foreground shrink-0">{technology}</span>

      {/* Data-session (bearer) status chip. The whole phone UI is gated on this
          reaching 'ready' — this strip tells the user why it's still loading. */}
      {dataPlaneStatus !== 'idle' && (
        <span
          className={cn(
            'flex items-center gap-1 shrink-0 ml-auto rounded px-1.5 py-0.5',
            dataPlaneStatus === 'ready' && 'text-status-success bg-status-success-bg',
            dataPlaneStatus === 'preparing' && 'text-muted-foreground bg-muted',
            dataPlaneStatus === 'failed' && 'text-status-warning bg-status-warning-bg',
          )}
          title={
            dataPlaneStatus === 'ready' ? 'Cellular data session open'
              : dataPlaneStatus === 'preparing' ? 'Opening the cellular data session…'
              : 'Data session could not be pre-opened — it will be brought up on first load'
          }
        >
          {dataPlaneStatus === 'preparing' && (
            <span className="inline-block w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
          )}
          {dataPlaneStatus === 'ready' && (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-current" />
          )}
          {dataPlaneStatus === 'failed' && (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-current" />
          )}
          <span>
            {dataPlaneStatus === 'preparing' ? 'Opening data…'
              : dataPlaneStatus === 'ready' ? 'Data ready'
              : 'Data offline'}
          </span>
        </span>
      )}

      <span
        className={cn('flex items-center gap-0.5 shrink-0', dataPlaneStatus === 'idle' && 'ml-auto')}
        title={signalPct !== null ? `Signal ${signalPct}%` : 'Signal unknown'}
      >
        {[1, 2, 3, 4].map((b) => (
          <span
            key={b}
            className={cn('inline-block rounded-sm', b <= bars ? 'bg-foreground' : 'bg-muted')}
            style={{ width: 3, height: 4 + b * 2 }}
          />
        ))}
        {signalPct !== null && <span className="text-muted-foreground ml-1 tabular-nums">{signalPct}%</span>}
      </span>
    </div>
  );
};

const Step2PhoneUI: React.FC<Step2PhoneUIProps> = ({
  networkStatus,
  isRegistered,
  registrationMessage,
  lockId,
  modemImei,
  modemModel,
  dataPlaneStatus,
  onEndSession
}) => {
  // Phone height tracks viewport so the frame claims every vertical pixel
  // available on the user's monitor — the whole point of the compact chrome.
  const [phoneHeight, setPhoneHeight] = useState<number>(computePhoneHeight);
  useEffect(() => {
    const handler = () => setPhoneHeight(computePhoneHeight());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  // Width derived from height so the video area matches the 412:915 capture AR.
  const phoneWidth = computePhoneWidth(phoneHeight);

  // OS-nav handlers published by AppLauncher based on which app is open.
  // Empty at the launcher; populated when an app is active so Back / Home /
  // Recents all return to the launcher.
  const [navHandlers, setNavHandlers] = useState<PhoneNavHandlers>({});

  return (
    <TooltipProvider>
      <div className="flex flex-col p-3">
        {/* Modem + network info strip — spans the full dialog row above the
            phone + side icons so long IMEIs / operator names aren't truncated. */}
        {lockId && (
          <SessionInfoHeader
            networkStatus={networkStatus}
            modemImei={modemImei}
            modemModel={modemModel}
            dataPlaneStatus={dataPlaneStatus}
          />
        )}
        {/* Phone + side icons. Symmetric flex-1 spacers on either side keep the
            phone centered when the SessionInfoHeader widens the dialog (long
            operator name / data chip), while the right column's justify-end
            pins the lock/close icons to the dialog edge. When the dialog is at
            its min width there's no extra space, so the phone simply sits next
            to the icons. */}
        <div className="flex items-center gap-3 w-full">
          {/* Left spacer — mirrors the icon column so the phone centers. */}
          <div className="flex-1" />
          {/* Phone Frame — 390 CSS px wide (Pixel 7 mobile viewport) with
              viewport-responsive height. In-frame status bar hidden once the
              session is live since SessionInfoHeader above covers the same
              operator/tech/signal info. */}
          <PhoneFrame
            networkStatus={networkStatus || undefined}
            isRegistered={isRegistered}
            registrationMessage={registrationMessage}
            dataPreparing={dataPlaneStatus === 'preparing'}
            dataMessage="Opening data session…"
            width={phoneWidth}
            height={phoneHeight}
            hideStatusBar={!!lockId}
            navHandlers={navHandlers}
          >
            {/* App content — gated until both registered AND the data session
                is actually OPEN ('ready'). We block on 'preparing' AND 'failed'
                (and 'idle'): opening the Browser before the bearer is truly up
                drops the user into a dead browser that only works "sometime
                later" when the session finally connects. Only 'ready' unlocks.
                Prepare retries generously (below) so a slow-registering bearer
                gets time before we ever surface the failed state. */}
            <AppLauncher
              disabled={!isRegistered || dataPlaneStatus !== 'ready'}
              onNavHandlersChange={setNavHandlers}
            />
          </PhoneFrame>

          {/* Side icons — flex-1 column with justify-end pins them to the right
              edge while balancing the left spacer that centers the phone. */}
          <div className="flex-1 flex justify-end">
          <div className="flex flex-col gap-3">
          {/* Lock status indicator */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  lockId
                    ? 'bg-status-success-bg text-status-success'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {lockId ? (
                  <Lock className="w-5 h-5" />
                ) : (
                  <Unlock className="w-5 h-5" />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{lockId ? 'Session locked' : 'No lock acquired'}</p>
            </TooltipContent>
          </Tooltip>

          {/* End Session button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onEndSession}
                className="w-10 h-10 rounded-full bg-muted hover:bg-destructive/10 hover:text-destructive flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>End session</p>
            </TooltipContent>
          </Tooltip>
          </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

// ── Module-level session continuity (survives transient remounts) ──────────
// A live TLAM session holds a real modem allocation + interactive lock + (on a
// Pi-5) a warm Chromium browser. React can remount this modal transiently —
// e.g. an AuthContext token-refresh re-fire that briefly remounts the page
// subtree. If each mount allocated its own session we'd get two bugs that the
// captured console showed: the remounted instance POSTs /tlam/sessions and gets
// 409 "no free modem" (the previous allocation still holds the only modem at
// that location), and the unmounting instance releases the live session out
// from under the user ("kicked out for no reason").
//
// Storing the active session at module scope lets a remount REATTACH to the
// existing allocation instead of re-allocating or releasing it. Genuine
// teardown (End-session button, modal closed, tab closed, navigated away)
// releases after a short grace window during which no remount re-claims it.
let moduleActiveSession: TlamSessionStartResponse | null = null;
let moduleActiveSessionLocation: string | null = null;
let moduleActiveSessionApn: string | undefined;
let moduleReleaseTimer: ReturnType<typeof setTimeout> | null = null;

function cancelPendingTlamRelease(): void {
  if (moduleReleaseTimer) {
    clearTimeout(moduleReleaseTimer);
    moduleReleaseTimer = null;
  }
}

/** Hard-release the module-level session immediately (explicit End-session). */
function releaseModuleSessionNow(orgId: string): void {
  cancelPendingTlamRelease();
  const s = moduleActiveSession;
  moduleActiveSession = null;
  moduleActiveSessionLocation = null;
  moduleActiveSessionApn = undefined;
  if (s) {
    endTlamSession(s.sessionId, s.allocationId, s.lockId, orgId)
      .catch((e) => console.error('[TestLikeAMobileModal] release failed:', e));
  }
}

/**
 * Schedule a release of the module-level session after a grace window. If the
 * modal remounts and reattaches within the window it cancels this timer, so a
 * transient remount never tears the session down. Only a real teardown (no
 * remount) lets the timer fire.
 */
function scheduleModuleSessionRelease(orgId: string, graceMs = 6000): void {
  cancelPendingTlamRelease();
  if (!moduleActiveSession) return;
  moduleReleaseTimer = setTimeout(() => {
    moduleReleaseTimer = null;
    releaseModuleSessionNow(orgId);
  }, graceMs);
}

/**
 * Main Modal Component
 */
export const TestLikeAMobileModal: React.FC<TestLikeAMobileModalProps> = ({
  isOpen,
  onClose,
  autoStart
}) => {
  const { activeOrganizationId, activeOrganization, currentUser } = useAuth();
  const { error: toastError, info: toastInfo } = useToast();
  const organizationId = activeOrganizationId || '';

  // Step 1: Selection state
  const [step, setStep] = useState<1 | 2>(1);
  const [profiles, setProfiles] = useState<ESimProfile[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [edgeDevices, setEdgeDevices] = useState<CentralEdgeDevice[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [selectedModemImei, setSelectedModemImei] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [lockAcquiring, setLockAcquiring] = useState(false);

  // Step 2: Phone UI state
  const [networkStatus, setNetworkStatus] = useState<any>(null);
  const [registrationMessage, setRegistrationMessage] = useState<string>('');

  // Backend-driven session — populated by startTlamSession() response.
  const [session, setSession] = useState<TlamSessionStartResponse | null>(null);
  const [lockError, setLockError] = useState<string | null>(null);

  // Cellular data-session (bearer) readiness. The phone UI stays in a loading
  // state until this is 'ready' so the user can't start browsing before the
  // first page can actually load. 'failed' still unlocks the UI — the edge's
  // inline http_fetch fallback can bring the bearer up on the first fetch.
  const [dataPlaneStatus, setDataPlaneStatus] =
    useState<'idle' | 'preparing' | 'ready' | 'failed'>('idle');

  // Refs for cleanup hooks (heartbeat + allocation watcher + pagehide beacon).
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Token billing: warn once when the balance runs low; end on the server's signal.
  const lowBalanceWarnedRef = useRef(false);
  const allocationUnsubRef = useRef<Unsubscribe | null>(null);
  const beaconHandlerRef = useRef<(() => void) | null>(null);
  // Freshest auth token for the pagehide beacon (refreshed every heartbeat —
  // a token captured once at session start expires within the hour).
  const beaconTokenRef = useRef<string | null>(null);
  // Set true on session end so the (long) data-plane prepare retry loop bails
  // out instead of flipping state on a torn-down session.
  const dataPlaneCancelledRef = useRef<boolean>(false);
  // Mirror of `session` for the unmount cleanup (whose closure would otherwise
  // capture a stale value). Used to release a still-live allocation if the
  // component unmounts without handleEndSession (route change / parent removes
  // the modal) — otherwise the modem + warm browser leak and the next open
  // hits "no free modem".
  const sessionRef = useRef<typeof session>(null);

  // Keep sessionRef current for the unmount cleanup below.
  useEffect(() => { sessionRef.current = session; }, [session]);

  // Unmount safety-net. The modal can unmount two ways: (1) a transient remount
  // (e.g. an AuthContext token-refresh re-fire remounting the page subtree) —
  // the session must SURVIVE so the remount reattaches to it; (2) a genuine
  // teardown (route change without End-session, parent removes the modal). We
  // can't tell which at unmount time, so we SCHEDULE a release after a grace
  // window: if a remount reattaches within it (startBackendSession cancels the
  // timer), the live session is preserved; otherwise it releases so the modem +
  // warm browser don't leak into the next open ("no free modem"). Detach the WS
  // peer immediately either way — the remount re-connects. Mount-once.
  useEffect(() => {
    return () => {
      if (sessionRef.current && moduleActiveSession) {
        try { phoneWebSocketService.disconnect(); } catch { /* no-op */ }
        scheduleModuleSessionRelease(organizationId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data on mount
  useEffect(() => {
    if (isOpen && step === 1) {
      loadData();
    }
  }, [isOpen, step, organizationId]);

  // On entering step 2, ask the backend to allocate a modem (same path as
  // scheduled flows) and start the interactive session.
  useEffect(() => {
    if (step === 2 && selectedLocationId && !session) {
      startBackendSession();
    }
    return () => {
      phoneWebSocketService.off('phone_network_status', handleNetworkStatus);
      phoneWebSocketService.off('phone_page_status', handlePageStatus);
    };
  }, [step, selectedLocationId]);

  // Auto-start: Pre-populate eSIM + Location and skip to step 2. Modem/device
  // selection is now backend-driven, so we ignore any modem IDs the caller
  // passed (kept optional for backwards compatibility).
  useEffect(() => {
    if (isOpen && autoStart && step === 1) {
      console.log('[TestLikeAMobileModal] Auto-starting with config:', autoStart);
      setSelectedProfileId(autoStart.eSimProfileId);
      setSelectedLocationId(autoStart.locationId);
      setStep(2);
    }
  }, [isOpen, autoStart]);

  const loadData = async () => {
    try {
      setLoadingData(true);

      // Load profiles, locations, and edge devices in parallel
      // Match Step2Locations pattern - call getLocations() with NO params to get all locations
      const [profilesData, locationsData, devicesData] = await Promise.all([
        getProfilesForOrg(organizationId),
        getLocations(),  // No params - get all locations (matches Step2Locations pattern)
        edgeDeviceService.getDevices()
      ]);

      // Shared inventory (super-admin resident SIMs) for granted orgs — merged
      // into the profile list; the picker renders them under a "Shared eSIMs"
      // group (each is flagged `shared`).
      let mergedProfiles = profilesData;
      if (canOrgUseSharedEsims(activeOrganization as any)) {
        const shared = await getSharedProfiles().catch(() => []);
        const seen = new Set(profilesData.map(p => p.id));
        mergedProfiles = [...profilesData, ...shared.filter(p => !seen.has(p.id))];
      }
      setProfiles(mergedProfiles);
      setLocations(locationsData);
      setEdgeDevices(devicesData);

      console.log('[TestLikeAMobileModal] Loaded data:', {
        profiles: profilesData.length,
        locations: locationsData.length,
        edgeDevices: devicesData.length
      });
    } catch (error) {
      console.error('[TestLikeAMobileModal] Error loading data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  // Arm the per-mount session lifecycle (Firestore allocation watcher + TTL
  // heartbeat + tab-close beacon) against a session. Shared by the fresh-start
  // and the remount-reattach paths so both behave identically.
  const armSessionLifecycle = async (resp: TlamSessionStartResponse) => {
    // Live view of the allocation — if the backend releases the session
    // (admin force-release, TTL expiry, another tab closing it), the modal
    // closes promptly.
    allocationUnsubRef.current = onSnapshot(
      doc(firestoreDb, 'modem_allocations', resp.allocationId),
      (snap) => {
        if (!snap.exists()) {
          handleSessionEndedRemotely('Session ended');
          return;
        }
        const data = snap.data();
        if (data?.status === 'released') {
          handleSessionEndedRemotely('Session ended by server');
          return;
        }
        const exp = data?.expiresAt?.toDate?.();
        if (exp && exp.getTime() <= Date.now()) {
          handleSessionEndedRemotely('Session expired');
        }
      },
      (err) => console.warn('[TestLikeAMobileModal] allocation watcher error:', err),
    );

    // Liveness keep-alive while the modal is open (see HEARTBEAT_INTERVAL_MS).
    // Each beat also refreshes the pagehide beacon's auth token: the previous
    // design captured the token ONCE at session start, and Firebase ID tokens
    // live ≤60 min (often only minutes when captured) — so the close-tab
    // beacon frequently fired with an expired token and died as a silent 401,
    // leaking the session (#165). getIdToken() returns the SDK's cached token
    // and auto-refreshes near expiry, so this is cheap.
    heartbeatTimerRef.current = setInterval(async () => {
      try {
        const hb = await heartbeatTlamSession(resp.sessionId, resp.allocationId, resp.lockId);
        if (hb.endSession) {
          toastInfo('Out of tokens — ending this session.');
          onClose();
          return;
        }
        if (hb.tokenWarning === 'low_balance' && !lowBalanceWarnedRef.current) {
          lowBalanceWarnedRef.current = true;
          toastInfo('Low token balance — this session will end soon when your tokens run out.');
        }
        const fresh = (await auth.currentUser?.getIdToken()) ?? null;
        if (fresh) beaconTokenRef.current = fresh;
      } catch (e) {
        console.warn('[TestLikeAMobileModal] heartbeat failed:', e);
      }
    }, HEARTBEAT_INTERVAL_MS);

    // Best-effort release on tab close / reload — reads the freshest token
    // from the ref (kept current by the heartbeat above).
    try {
      const token = (await auth.currentUser?.getIdToken()) ?? null;
      if (token) {
        beaconTokenRef.current = token;
        const handler = () => {
          const t = beaconTokenRef.current;
          if (t) endTlamSessionBeacon(resp.sessionId, resp.allocationId, resp.lockId, t, organizationId);
        };
        beaconHandlerRef.current = handler;
        window.addEventListener('pagehide', handler);
      }
    } catch { /* non-fatal */ }
  };

  /**
   * Ask the backend to allocate a modem for an interactive TLAM session using
   * the same code path scheduled flows use (QueueService.findFreeModem +
   * resourceManager.tryAllocateModem). Returns sessionId, allocationId,
   * lockId, and the modem/device the server picked.
   */
  const startBackendSession = async () => {
    if (!selectedLocationId || !organizationId || !currentUser?.uid) return;

    // Reattach to a live session from a prior mount (remount-safe). A transient
    // remount must NOT allocate a fresh modem (409 — the previous allocation
    // still holds the only modem at this location) nor disturb the existing
    // one. Cancel any pending grace-release and re-arm the local lifecycle
    // against the existing session instead of POSTing /tlam/sessions again.
    if (moduleActiveSession && moduleActiveSessionLocation === selectedLocationId) {
      cancelPendingTlamRelease();
      const resp = moduleActiveSession;
      const reuseApn = moduleActiveSessionApn;
      console.log('[TestLikeAMobileModal] ♻️ Reattaching to live session', resp.sessionId);
      phoneWebSocketService.on('phone_network_status', handleNetworkStatus);
      phoneWebSocketService.on('phone_page_status', handlePageStatus);
      setSession(resp);
      setLockError(null);
      setDataPlaneStatus('ready'); // bearer was prepared on the original start
      phoneWebSocketService.connect(
        resp.deviceId, resp.sessionId, resp.modemImei, organizationId,
        resp.lockId, resp.allocationId, selectedProfileId || undefined, reuseApn,
      );
      try {
        await phoneWebSocketService.startSession(
          resp.deviceId, resp.modemImei, currentUser.uid, organizationId,
          selectedProfileId || undefined,
        );
      } catch (e) { console.warn('[TestLikeAMobileModal] reattach startSession failed:', e); }
      await armSessionLifecycle(resp);
      return;
    }

    try {
      phoneWebSocketService.on('phone_network_status', handleNetworkStatus);
      phoneWebSocketService.on('phone_page_status', handlePageStatus);

      console.log('[TestLikeAMobileModal] Requesting TLAM session for location', selectedLocationId);

      // Resolve the session APN. Quick Tests Step 3 now collects a mandatory
      // APN and passes it via autoStart.apn — that's the single source of truth.
      // Passing it to startTlamSession lets the backend warm `data_plane_prepare`
      // at allocation time (before anything else), so the first URL load is
      // ~1-2 s instead of paying a cold ~18 s bring-up. Fall back to the
      // per-profile cache only when the modal is opened without an autoStart
      // APN (rare — direct/legacy entry); the dialog still prompts if neither
      // is present.
      let sessionApn: string | undefined = autoStart?.apn?.trim() || undefined;
      if (!sessionApn) {
        try {
          const apnKey = `simcheck.quickTests.inputs.tlam.browser.${selectedProfileId || 'default'}`;
          const raw = localStorage.getItem(apnKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (typeof parsed?.apn === 'string' && parsed.apn.trim()) {
              sessionApn = parsed.apn.trim();
            }
          }
        } catch { /* ignore parse errors — dialog will prompt */ }
      }

      const resp = await startTlamSession({
        locationId: selectedLocationId,
        organizationId,
        // When the wizard handed us a group, send the group and let the backend
        // resolve the member for the modem it allocates. The client-picked
        // profile is only a representative — it may live on a different modem.
        // Otherwise (manual modal), send the explicitly-selected profile.
        eSimProfileId: autoStart?.eSimGroupId ? undefined : (selectedProfileId || undefined),
        eSimGroupId: autoStart?.eSimGroupId,
        apn: sessionApn,
        forcedNetworkMccMnc: autoStart?.forcedNetworkMccMnc,
        forcedRat: autoStart?.forcedRat,
      });
      console.log('[TestLikeAMobileModal] ✅ Session started:', resp);
      // Adopt the backend-resolved APN when the client didn't supply one
      // (issue #161): the server resolves profile.apn → group.apn → network
      // defaultApn — for group sessions only it knows the concrete member.
      // It also fired the eager prepare with this value, so the modal's
      // awaited prepare below just rides the same bring-up.
      const effectiveApn = sessionApn || (resp.apn ? String(resp.apn).trim() : undefined) || undefined;
      if (!sessionApn && effectiveApn) {
        console.log(`[TestLikeAMobileModal] using eSIM-resolved APN "${effectiveApn}" from session response`);
      }
      // Promote to the module-level holder so a transient remount reattaches
      // instead of allocating a new modem / killing this one. Cancel any
      // pending grace-release from a just-fired unmount.
      moduleActiveSession = resp;
      moduleActiveSessionLocation = selectedLocationId;
      moduleActiveSessionApn = effectiveApn;
      cancelPendingTlamRelease();
      setSession(resp);
      setLockError(null);

      // Register the session with the phone service. All UI gestures
      // (dial / SMS / browser fetch) pipe through executeFlow with
      // interactiveLockId=resp.lockId, unchanged.
      phoneWebSocketService.connect(
        resp.deviceId,
        resp.sessionId,
        resp.modemImei,
        organizationId,
        resp.lockId,
        resp.allocationId,
        selectedProfileId || undefined,
        effectiveApn,
      );

      // Read last-known modem network status into the UI.
      await phoneWebSocketService.startSession(
        resp.deviceId,
        resp.modemImei,
        currentUser.uid,
        organizationId,
        selectedProfileId || undefined,
      );

      // Open the cellular data session up front and keep the phone UI in a
      // loading state until it's warm. The backend already kicked off a
      // `data_plane_prepare` at allocation; this awaited call rides the edge's
      // per-IMEI lock (idempotent — returns as soon as that bring-up finishes)
      // so we get a real "bearer ready" signal to unlock the UI. Without the
      // APN we can't prepare, so we unlock immediately and let the first
      // http_fetch do an inline bring-up.
      if (effectiveApn) {
        const apnReady: string = effectiveApn;
        dataPlaneCancelledRef.current = false;
        setDataPlaneStatus('preparing');
        // Retry the prepare a few times before declaring the data session
        // offline. The first attempt can legitimately fail when the modem is
        // still recovering from a clean-on-release CFUN reset (cfunResetOnRelease
        // modems reboot on session-end, then need ~40-50s to re-register) or on
        // a transient QMI "Client ID not released" stale-bearer error — both
        // self-heal, and a retry rides the edge per-IMEI lock so it returns as
        // soon as a concurrent/earlier bring-up succeeds. Only after all
        // attempts fail do we show "Data offline" (UI still unlocks; the first
        // http_fetch falls back to an inline bring-up).
        (async () => {
          // Be patient: a roaming bearer recovering from a flap (or a
          // cfunResetOnRelease modem rebooting on session-end) can take 40-80s
          // to register. Apps stay blocked the whole time (gate is === 'ready'),
          // so a long wait just means "still opening data session…", never a
          // dead browser. Cancel cleanly if the modal/session goes away.
          const PREPARE_ATTEMPTS = 8;
          const RETRY_DELAY_MS = 6000;   // ~8 × (call + 6s) ≈ up to ~60-90s budget
          for (let attempt = 1; attempt <= PREPARE_ATTEMPTS; attempt++) {
            if (dataPlaneCancelledRef.current) return;
            try {
              const r = await prepareTlamSession(resp.sessionId, resp.allocationId, apnReady);
              if (dataPlaneCancelledRef.current) return;
              console.log(
                `[TestLikeAMobileModal] ✅ data session ready apn=${apnReady} ` +
                `attempt=${attempt} piMs=${r.piDurationMs ?? '?'} totalMs=${r.totalDurationMs ?? '?'}`
              );
              setDataPlaneStatus('ready');
              return;
            } catch (err) {
              console.warn(
                `[TestLikeAMobileModal] data session prepare attempt ${attempt}/${PREPARE_ATTEMPTS} failed:`,
                err
              );
              if (attempt < PREPARE_ATTEMPTS) {
                await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
              }
            }
          }
          if (dataPlaneCancelledRef.current) return;
          // Genuinely couldn't bring the bearer up after the full budget —
          // surface 'failed'. Apps stay blocked (gate is === 'ready'); the chip
          // shows "Data offline" and the user can End session / retry rather
          // than land in a browser that can't load anything.
          setDataPlaneStatus('failed');
        })();
      } else {
        setDataPlaneStatus('ready');
      }

      await armSessionLifecycle(resp);
    } catch (error: any) {
      console.error('[TestLikeAMobileModal] ❌ Session start failed:', error);
      const msg = error?.message || String(error);
      setLockError(msg);
      // `lockError` state isn't rendered anywhere, so without a toast the user
      // sees nothing — just a modal that briefly opens and vanishes. Surface
      // the real server message ("No free modem at location …", 409s, etc.)
      // so they know why.
      try { toastError(`Test Like A Mobile: ${msg}`); } catch { /* no-op */ }
      handleEndSession();
    }
  };

  /**
   * Called when Firestore tells us the session is gone (admin force-release,
   * TTL expiry, another tab closed it). Shows a toast and closes the modal.
   */
  const handleSessionEndedRemotely = (reason: string) => {
    console.log('[TestLikeAMobileModal] Session ended remotely:', reason);
    try { toastInfo(`Test Like A Mobile: ${reason}`); } catch { /* no-op */ }
    handleEndSession();
  };

  const handleNetworkStatus = (data: any) => {
    // Sticky merge: once the modem has reported a registered network, keep
    // the last-known operator/technology even if a subsequent heartbeat
    // comes back with those fields empty. The modem occasionally drops
    // the operator field for a single heartbeat (e.g. mid-call CFUN cycle,
    // forced PLMN-lock format-latch transitions) — without the merge that
    // would flip `registered` to false and pop the "Connecting to
    // network..." overlay over a working call. Status, signalQuality, and
    // any other fresh fields always win.
    setNetworkStatus((prev: any) => {
      if (!prev) return data;
      const merged = { ...prev, ...data };
      if (!data.operator && prev.operator) merged.operator = prev.operator;
      if (!data.technology && prev.technology) merged.technology = prev.technology;
      // `registered` is sticky-true once we've seen it: a transient false
      // shouldn't unregister us. Will reset on session end via setNetworkStatus(null).
      if (prev.registered && !data.registered) merged.registered = true;
      return merged;
    });
    if (data.status === 'registering' && data.message) {
      setRegistrationMessage(data.message);
    } else if (data.status === 'connected') {
      setRegistrationMessage('');
    } else if (data.status === 'failed') {
      setRegistrationMessage('No service');
    }
  };

  // A page actually loading is ground truth that the data session works, no
  // matter what the prepare RPC reported. Flip the chip to 'ready' so we never
  // show a stale "Data offline" while the user is browsing fine — this happens
  // when the bearer comes up via a slow post-CFUN-reset recovery (~70-80s)
  // after the modal's prepare retries already gave up.
  const handlePageStatus = (data: any) => {
    if (data?.status === 'downloading' || data?.status === 'loaded') {
      setDataPlaneStatus((prev) => (prev === 'ready' ? prev : 'ready'));
    }
  };

  const handleStartSession = () => {
    // Validation check - all selections should already be made
    if (!selectedProfileId || !selectedLocationId || !selectedDeviceId || !selectedModemImei) {
      console.error('[TestLikeAMobileModal] Missing selections:', {
        profile: selectedProfileId,
        location: selectedLocationId,
        device: selectedDeviceId,
        modem: selectedModemImei
      });
      return;
    }

    console.log('[TestLikeAMobileModal] Starting session with selections:', {
      profileId: selectedProfileId,
      locationId: selectedLocationId,
      deviceId: selectedDeviceId,
      modemImei: selectedModemImei
    });

    // Proceed to step 2 (lock will be acquired via useLockSession hook)
    setStep(2);
  };

  const handleLocationChange = (locationId: string) => {
    setSelectedLocationId(locationId);
    // Reset dependent selections when location changes
    setSelectedDeviceId(null);
    setSelectedModemImei(null);
  };

  const handleDeviceChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    // Reset modem selection when device changes
    setSelectedModemImei(null);
  };

  const handleEndSession = () => {
    // Cancel any in-flight data-plane prepare retry loop so it can't flip
    // dataPlaneStatus after teardown.
    dataPlaneCancelledRef.current = true;
    // Stop heartbeat
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    // Stop Firestore watcher
    if (allocationUnsubRef.current) {
      allocationUnsubRef.current();
      allocationUnsubRef.current = null;
    }
    // Detach pagehide beacon
    if (beaconHandlerRef.current) {
      window.removeEventListener('pagehide', beaconHandlerRef.current);
      beaconHandlerRef.current = null;
    }

    // Explicit teardown: release the backend session NOW (clears the
    // module-level holder + cancels any pending grace-release) so the modem +
    // warm browser are freed and a remount can't reattach to a dead session.
    // Fire-and-forget — the UI closes immediately regardless.
    if (session || moduleActiveSession) {
      try { phoneWebSocketService.endSession(); } catch { /* no-op */ }
    }
    releaseModuleSessionNow(organizationId);
    phoneWebSocketService.disconnect();

    setSession(null);
    setStep(1);
    setSelectedProfileId(null);
    setSelectedLocationId(null);
    setSelectedDeviceId(null);
    setSelectedModemImei(null);
    setNetworkStatus(null);
    setRegistrationMessage('');
    setDataPlaneStatus('idle');

    onClose();
  };

  // Lock extension no longer needed - lock managed by WebSocket lifecycle

  // Step 1: Use Modal with header
  if (step === 1) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleEndSession}
        title="Test Like A Mobile"
        size="md"
      >
        <Step1Selection
          profiles={profiles}
          locations={locations}
          edgeDevices={edgeDevices}
          selectedProfileId={selectedProfileId}
          selectedLocationId={selectedLocationId}
          selectedDeviceId={selectedDeviceId}
          selectedModemImei={selectedModemImei}
          onProfileChange={setSelectedProfileId}
          onLocationChange={handleLocationChange}
          onDeviceChange={handleDeviceChange}
          onModemChange={setSelectedModemImei}
          onNext={handleStartSession}
          loading={loadingData || lockAcquiring}
        />
      </Modal>
    );
  }

  // Step 2: Use Dialog directly without header for compact phone UI
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleEndSession()}>
      <DialogContent
        className="max-w-fit max-h-[95vh] p-0 gap-0 overflow-y-auto overflow-x-hidden [&>button]:hidden"
        // A live TLAM session holds a real modem lock + data bearer — closing it
        // is destructive (releases the allocation). Don't let an accidental
        // click-outside or Escape end it; only the explicit "End session"
        // button should.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <Step2PhoneUI
          networkStatus={networkStatus}
          isRegistered={networkStatus?.registered || false}
          registrationMessage={registrationMessage}
          lockId={session?.lockId ?? null}
          modemImei={session?.modemImei ?? null}
          modemModel={session?.modemModel ?? null}
          dataPlaneStatus={dataPlaneStatus}
          onEndSession={handleEndSession}
        />
      </DialogContent>
    </Dialog>
  );
};

export default TestLikeAMobileModal;
